import React, { FC, memo, useCallback, useRef } from 'react';
import styled from '@emotion/styled';
import { useButton, useI18n, useLogger } from '~Hooks';
import Icon from '~ComponentsImproved/Icon';
import expandMoreIcon from '../../../../../Assets/icons/expand_more.svg';
import { FormattedModificationGroup } from '~Hooks/ItemModifications/useItemModifications';
import { MODIFICATION_GROUP_TYPE } from '~../__queries__/globalTypes';
import VariationOption from './VariationOption';

const Group = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;

  /** Accordion: spacing moves into the parts so nothing leaves a gap while folded. */
  &.is-collapsible {
    gap: 0;
  }
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  /** The whole header row is the toggle in an accordion. */
  .is-collapsible & {
    cursor: pointer;
  }
`;

/** Title and hints on one line, the picks summary under them. */
const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

/** Wraps the hints under the title on a narrow pane instead of squeezing them into two-line blocks. */
const HeaderLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 9px;
`;

/** What is picked, shown only while folded - slides up into view as the body closes. */
const Summary = styled.span`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--eposText);
  opacity: 0;
  max-height: 0;
  /* Spacing lives here, not as a gap, so the open header is just the title line and centers on the button. */
  margin-top: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  transform: translate3d(0, -4px, 0);
  transition: opacity 0.25s ease-out, transform 0.25s ease-out, max-height 0.25s ease-out,
    margin-top 0.25s ease-out;

  .is-collapsed & {
    opacity: 0.7;
    max-height: 20px;
    margin-top: 3px;
    transform: translate3d(0, 0, 0);
  }
`;

/** Smooth, slightly springy ease for everything that opens and closes. */
const EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

/** Height animation without measuring: a one-row grid going 1fr -> 0fr. */
const Collapse = styled.div`
  display: grid;
  grid-template-rows: 1fr;
  transition: grid-template-rows 0.4s ${EASE};

  .is-collapsed & {
    grid-template-rows: 0fr;
  }
`;

/** Space between the header and the options. */
const OPTIONS_TOP = 12;

const CollapseInner = styled.div`
  min-height: 0;
  overflow: hidden;
  /* Room for option borders and the pressed scale at the clip edge. */
  margin: 0 -2px;

  > * {
    padding: ${OPTIONS_TOP}px 2px 2px;
  }
`;

/**
 * Round chevron button at the right of the header. Points down while folded,
 * up when open. The tap belongs to the header row - this only shows it.
 */
const ToggleCircle = styled.span`
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid var(--eposBorderColor);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  transition: transform 0.15s ease-out;

  .is-touching > & {
    transform: scale(0.92);
  }
`;

/** The chevron inside; flips with the fold. */
const ToggleChevron = styled.span`
  display: flex;
  transform: rotate(180deg);
  transition: transform 0.4s ${EASE};

  .is-collapsed & {
    transform: rotate(0deg);
  }
`;

const GroupTitle = styled.h3`
  margin: 0;
  /* Tight line box, so centering lines up the glyphs and not the inherited leading. */
  line-height: 1.2;
  font-size: 21px;
  font-weight: 700;
  color: var(--eposText);

  /* As its own page the title steps up to match the upsell page title. */
  .is-page & {
    font-size: 31px;
    font-weight: 800;
    letter-spacing: -0.7px;
  }
`;

/**
 * Box-centered, Figtree lines up cap heights - but titles are mostly lowercase,
 * whose middle sits 0.1em lower. Nudge the hints down by the difference so the
 * lowercase of both lines up with the title.
 */
const GroupHint = styled.span`
  position: relative;
  top: calc((21px - 12px) * 0.1);
  line-height: 1.2;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 600;
  opacity: 0.55;
  color: var(--eposText);

  /** Same nudge against the bigger page title. */
  .is-page & {
    top: calc((31px - 12px) * 0.1);
  }
`;

/** "Choose one" / "Choose as many as you like" - how the group works, next to whether it is required. */
const ChoiceHint = styled(GroupHint)`
  &::before {
    content: '·';
    margin-right: 12px;
  }
`;

/**
 * "Required" / "Optional". Always the same pill with the same padding, so the
 * header never changes height; only the colors move. `is-pending` (required
 * and not yet satisfied) fills it in the button color - a visible to-do that
 * fades to plain text the moment a choice is made.
 */
const StatusHint = styled(GroupHint)`
  padding: 3px 9px;
  border-radius: 999px;
  /* Same color as the pane, so the pill is there but invisible until it fills. */
  background: var(--eposSurface);
  transition: background-color 0.2s ease-out, color 0.2s ease-out, opacity 0.2s ease-out;

  &.is-pending {
    opacity: 1;
    background: var(--eposButton);
    color: var(--eposButtonText);
  }
`;

const Options = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 9px;
`;

interface Props {
  group: FormattedModificationGroup;
  onSelect: (id: string) => void;
  /** True when the group is a page of its own (oneModificationGroupPerPage) - bigger title. */
  asPage?: boolean;
  /** Fold into an accordion unless backoffice marks the group expandedByDefault. Off in per-page mode. */
  collapsible?: boolean;
}

/**
 * One modification group: title with required/choice hints, then its options.
 * When collapsible and not expandedByDefault it is an accordion - folded at
 * first, unless it is required and nothing is picked yet. Folding is a class
 * on the section via ref, so a tap never re-renders.
 */
const ModificationGroupSection: FC<Props> = memo(({ group, onSelect, asPage, collapsible }) => {
  const { i18n } = useI18n();
  const logger = useLogger('epos-modification-group');
  const groupRef = useRef<HTMLElement>(null);

  const isAccordion = !!collapsible && !asPage && !group.expandedByDefault;

  /** Decided once at mount - later renders must not fight the shopper's own taps. */
  const startCollapsedRef = useRef(isAccordion && group.valid);

  const handleToggle = useCallback(() => {
    const node = groupRef.current;
    if (!node) {
      return;
    }
    const collapsed = node.classList.toggle('is-collapsed');
    logger.info('Modification group toggled', { groupId: group.id, collapsed });
  }, [group.id]);

  const { touching, disabled, disabledToast, ...toggleButton } = useButton({
    onClick: handleToggle,
  });

  const summary = group.variations
    .filter((variation) => variation.selected)
    .map((variation) => variation.name)
    .join(', ');

  const groupClassName = [
    asPage && 'is-page',
    isAccordion && 'is-collapsible',
    isAccordion && startCollapsedRef.current && 'is-collapsed',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Group
      ref={groupRef}
      className={groupClassName || undefined}
      data-testid="epos-modification-group">
      <GroupHeader
        {...(isAccordion ? toggleButton : {})}
        className={isAccordion && touching ? 'is-touching' : undefined}
        data-testid="epos-group-header">
        <HeaderText>
          <HeaderLine>
            <GroupTitle>{group.title}</GroupTitle>
            <StatusHint
              className={group.required && !group.valid ? 'is-pending' : undefined}
              data-testid="epos-group-status">
              {group.required ? i18n('Express.Item.Required') : i18n('Express.Item.Optional')}
            </StatusHint>
            <ChoiceHint data-testid="epos-group-choice">
              {group.type === MODIFICATION_GROUP_TYPE.SINGLE
                ? i18n('Express.Item.ChooseOne')
                : i18n('Express.Item.ChooseMany')}
            </ChoiceHint>
          </HeaderLine>
          {/* Only when something is picked - an empty line would push the title off center. */}
          {isAccordion && summary && <Summary data-testid="epos-group-summary">{summary}</Summary>}
        </HeaderText>
        {isAccordion && (
          <ToggleCircle data-testid="epos-group-toggle">
            <ToggleChevron>
              <Icon src={expandMoreIcon} color="var(--eposText)" width="24px" height="24px" />
            </ToggleChevron>
          </ToggleCircle>
        )}
      </GroupHeader>
      <Collapse>
        <CollapseInner>
          <Options>
            {group.variations.map((variation) => (
              <VariationOption
                key={variation.id}
                variation={variation}
                groupType={group.type}
                onSelect={onSelect}
              />
            ))}
          </Options>
        </CollapseInner>
      </Collapse>
    </Group>
  );
});

ModificationGroupSection.displayName = 'ModificationGroupSection';

export default ModificationGroupSection;
